import type { HugeNumber } from "@/shared/numbers";
import { hugeZero } from "@/shared/numbers";
import type {
  HoverIdleEffectUnlockState,
  HoverIdleGridTier,
  MHovState,
  NHovState,
  OHovState,
} from "../types";
import {
  HOV_CELL_BASE_COSTS,
  createDefaultHovCells,
  type HovCellState,
} from "./hoverIdleState";
import { createDefaultHoverIdleEffectUnlockState } from "@/features/hoverIdle/unlocks/hoverIdleEffectUnlocks";
import { createDefaultMHovState } from "../mHov/mHovLogic";
import { createDefaultNHovState } from "../nHov/nHovLogic";
import {
  createEmptyHovAmplifierGaugeVisualSegments,
  createEmptyHovAmplifierRecentChargeSources,
  createEmptyHovAmplifierRecentDirectChargeAtMs,
  resetHovAmplifierForOHov,
  type HovAmplifierGaugeVisualChargeBatch,
  type HovAmplifierGaugeVisualSegments,
  type HovAmplifierRecentChargeSources,
  type HovAmplifierRecentDirectChargeAtMs,
  type HovAmplifierState,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import type {
  HoverResetProgressScope,
  MHovResetPlan,
  NHovResetPlan,
  OHovResetPlan,
} from "./hoverResetPlans";

// Reset-state applier. Owns the field-level mutation answer to
// "given a reset plan and the current state groups, what does each group
// become after this reset?". The model keeps reset orchestration, but the
// per-scope and per-grant transitions live here so reset coverage of Hov /
// Tim / MHov / NHov / OHov / Amplifier / Hov target grid / secret progress
// timers can be read in one place.

export interface HovTargetGridResetGroup {
  readonly hovGridTier: HoverIdleGridTier;
  readonly hovHoveringCellIndex: number | null;
  readonly isHoveringHov: boolean;
  readonly hoverIdleEffectUnlocks: HoverIdleEffectUnlockState;
  readonly hovCells: HovCellState[];
}

export interface SecretProgressResetGroup {
  readonly continuousPointerSeconds: number;
  readonly observerSeconds: number;
  readonly xPatternSeconds: number;
  readonly hPatternSeconds: number;
  readonly hovOLabelHoverSeconds: number;
  readonly hovCellClickTimestamps: number[];
}

export interface HovAmplifierResetGroup {
  readonly hovAmplifierState: HovAmplifierState;
  readonly hovAmplifierRecentChargeSources: HovAmplifierRecentChargeSources;
  readonly hovAmplifierRecentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs;
  readonly hovAmplifierGaugeVisualSegments: HovAmplifierGaugeVisualSegments;
  readonly hovAmplifierVisualChargeBatch: HovAmplifierGaugeVisualChargeBatch;
}

export interface HoverResetProgressStateGroups {
  readonly hov: HugeNumber;
  readonly hiddenHovGainBonus: HugeNumber;
  readonly purchasedHovGainUpgrades: number;
  readonly hovUpgradeCost: HugeNumber;
  readonly timUpgradeCost: HugeNumber;
  readonly hovTargetGrid: HovTargetGridResetGroup;
  readonly hovGridThemeHue: number | null;
  readonly mHovState: MHovState;
  readonly nHovState: NHovState;
  readonly secretProgress: SecretProgressResetGroup;
  readonly hovAmplifier: HovAmplifierResetGroup;
}

export interface HoverResetProgressBaseValues {
  readonly baseUpgradeCost: HugeNumber;
  readonly baseTimUpgradeCost: HugeNumber;
}

export function createDefaultHovTargetGridResetGroup(): HovTargetGridResetGroup {
  return {
    hovGridTier: 1,
    hovHoveringCellIndex: null,
    isHoveringHov: false,
    hoverIdleEffectUnlocks: createDefaultHoverIdleEffectUnlockState(),
    hovCells: createDefaultHovCells(HOV_CELL_BASE_COSTS),
  };
}

export function createZeroedSecretProgressResetGroup(): SecretProgressResetGroup {
  return {
    continuousPointerSeconds: 0,
    observerSeconds: 0,
    xPatternSeconds: 0,
    hPatternSeconds: 0,
    hovOLabelHoverSeconds: 0,
    hovCellClickTimestamps: [],
  };
}

// Visual / transient amplifier slices are cleared, but persistent enhancement
// progress is preserved by `resetHovAmplifierForOHov`. Keep the visual batch
// drained in lockstep with the segments so the next tick starts from a clean
// frame.
export function resetHovAmplifierResetGroupForOHov(
  current: HovAmplifierResetGroup,
): HovAmplifierResetGroup {
  return {
    hovAmplifierState: resetHovAmplifierForOHov(current.hovAmplifierState),
    hovAmplifierRecentChargeSources:
      createEmptyHovAmplifierRecentChargeSources(),
    hovAmplifierRecentDirectChargeAtMs:
      createEmptyHovAmplifierRecentDirectChargeAtMs(),
    hovAmplifierGaugeVisualSegments:
      createEmptyHovAmplifierGaugeVisualSegments(),
    hovAmplifierVisualChargeBatch: {},
  };
}

export function applyHoverResetProgressScope(
  scope: HoverResetProgressScope,
  current: HoverResetProgressStateGroups,
  base: HoverResetProgressBaseValues,
): HoverResetProgressStateGroups {
  return {
    hov: scope.hovResource === "reset-to-zero" ? hugeZero() : current.hov,
    hiddenHovGainBonus:
      scope.hiddenHovGainBonus === "reset-to-zero"
        ? hugeZero()
        : current.hiddenHovGainBonus,
    purchasedHovGainUpgrades:
      scope.purchasedHovGainUpgrades === "reset-to-zero"
        ? 0
        : current.purchasedHovGainUpgrades,
    hovUpgradeCost:
      scope.hovUpgradeCost === "reset-to-base"
        ? base.baseUpgradeCost
        : current.hovUpgradeCost,
    timUpgradeCost:
      scope.timUpgradeCost === "reset-to-base"
        ? base.baseTimUpgradeCost
        : current.timUpgradeCost,
    hovTargetGrid:
      scope.hovTargetGrid === "reset-to-default"
        ? createDefaultHovTargetGridResetGroup()
        : current.hovTargetGrid,
    hovGridThemeHue:
      scope.hovGridThemeHue === "clear" ? null : current.hovGridThemeHue,
    mHovState:
      scope.mHovState === "reset-to-default"
        ? createDefaultMHovState()
        : current.mHovState,
    nHovState:
      scope.nHovState === "reset-to-default"
        ? createDefaultNHovState()
        : current.nHovState,
    secretProgress:
      scope.secretProgressTimers === "reset-to-zero"
        ? createZeroedSecretProgressResetGroup()
        : current.secretProgress,
    hovAmplifier:
      scope.hovAmplifier === "reset-gauge-and-ap-preserve-enhancement"
        ? resetHovAmplifierResetGroupForOHov(current.hovAmplifier)
        : current.hovAmplifier,
  };
}

// Layer grants describe the layer-specific state hand-off that happens after
// the shared progress scope has been resolved. They are split per layer so
// the model can keep its existing reset order (MHov: grant before scope;
// NHov / OHov: grant after scope).

export interface MHovResetGrantPatch {
  readonly mHovState: MHovState;
}

export function applyMHovResetGrant(plan: MHovResetPlan): MHovResetGrantPatch {
  return { mHovState: plan.mHovGrant.nextState };
}

export interface NHovResetGrantPatch {
  readonly hovGridThemeHue: number;
  readonly nHovState: NHovState;
}

export function applyNHovResetGrant(
  plan: NHovResetPlan,
  current: { readonly nHovState: NHovState },
): NHovResetGrantPatch {
  return {
    hovGridThemeHue: plan.nHovGrant.selectedThemeHue,
    nHovState: {
      ...current.nHovState,
      count: plan.nHovGrant.nextCount,
      branchExperienceByBranch: plan.nHovGrant.nextBranchExperienceByBranch,
      effectBoundaryCaps: plan.nHovGrant.nextEffectBoundaryCaps,
      continuousEffectProgressByBranch:
        plan.nHovGrant.nextContinuousEffectProgressByBranch,
    },
  };
}

export interface OHovResetGrantPatch {
  readonly oHovState: OHovState;
}

export function applyOHovResetGrant(
  plan: OHovResetPlan,
  current: { readonly oHovState: OHovState },
): OHovResetGrantPatch {
  return {
    oHovState: {
      ...current.oHovState,
      count: current.oHovState.count + plan.oHovGrant.countDelta,
      isUnlocked: true,
      power: current.oHovState.power.add(plan.oHovGrant.powerGain),
      hovCore: current.oHovState.hovCore.add(plan.oHovGrant.hovCoreGain),
      records: plan.oHovGrant.nextRunRecords,
    },
  };
}
